import React, { useState, useRef, useEffect, useMemo } from 'react';
import { SectionContainer } from '@components/SectionComponents/SectionContainer';
import { LeadershipProfilesSectionData, Profile } from '@models/section/leadershipProfilesSection';
import { Grid, Tab, Tabs, Stack, Skeleton } from '@mui/material';
import { colors, Heading, breakpoints, space, Text, LazyLoadContainer } from '@web-for-marketing/react-ui';
import { Picture } from '@components/Picture';
import { CSSObject } from '@emotion/react';
import { useAtom } from 'jotai';
import { isMobileAtom } from '@atoms/appSettings';
import { ImagePopout } from '@components/ImagePopout/ImagePopout';
import { keyIsEnter, keyIsSpace } from '@helpers/keyPressEventHelper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Keyboard, A11y } from 'swiper/modules';
import 'swiper/css';
import type { Swiper as SwiperType } from 'swiper';

interface LeadershipProfilesProps {
    sectionData: LeadershipProfilesSectionData;
    lazyLoad: boolean;
}

const classes = {
    cardContainer: (isClickable: boolean) =>
        (({
            maxWidth: 280,
            textAlign: 'center',
            cursor: isClickable ? 'pointer' : 'default',

            '&:hover': {
                '& h3': {
                    color: isClickable ? colors.text.activePrimary : colors.text.primary,
                },
                '& .profile-image': {
                    transform: isClickable ? 'scale(1.05)' : 'none',
                },
            },
        }) as const),
    imageWrapper: {
        borderRadius: '1.6rem',
        overflow: 'hidden',
        marginBottom: '1.6rem',
        aspectRatio: '1 / 1',
    },
    image: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        transition: 'transform 0.5s ease-in-out',
    },
    name: {
        marginBottom: '0.8rem',
    },
    fullWidth: {
        width: '100%',
        overflow: 'hidden',
    },
    tabPanel: (hide: boolean) =>
        (({
            [`@media (min-width: ${breakpoints.md})`]: {
                transition: 'opacity 0.5s ease-in-out',
                opacity: hide ? 0 : 1,
            },

            overflow: 'hidden',
            padding: '0 1rem',
        }) as const),
    scrollButtons: {
        '&.Mui-disabled': {
            display: 'none',
        },
    },
} as const;

function getRoundedTabStyles(): Record<string, CSSObject> {
    const roundedTabStyle = {
        '&.MuiTab-root': {
            textTransform: 'none',
            textWrap: 'balance',
            color: colors.text.activePrimary,
            fontSize: '2rem !important',
            fontWeight: 500,
            lineHeight: '1.3',
            borderRadius: '100px',
            padding: '1.2rem 3.6rem',
            zIndex: 100,
            '&:hover': {
                color: colors.text.activeSecondary,
            },
            '&:focus-visible': {
                outline: `1px solid ${colors.border.activePrimary}`,
                outlineOffset: '-1px',
            },
            [`@media (max-width: ${breakpoints.md})`]: {
                fontSize: '1.8rem !important',
                lineHeight: '1.4',
            },
        },
        '&.Mui-selected': {
            color: colors.text.activeSecondary,
        },
    } as const;

    return {
        container: {
            margin: '3.2rem 0 6.4rem 0',
            border: `1px solid ${colors.border.primary}`,
            borderRadius: '100px',
            padding: '8px 10px',
            maxWidth: '100%',
            backgroundColor: colors.background.white,
            [`@media (max-width: ${breakpoints.md})`]: {
                margin: '3.2rem 0',
                marginRight: `-${space.containerOuterPadding}`,
                marginLeft: `-${space.containerOuterPadding}`,
                border: 'none',
            },
        },
        tab: roundedTabStyle,
        indicator: {
            display: 'block',
            height: '100%',
            backgroundColor: colors.action.active,
            border: `1px solid ${colors.action.active}`,
            borderRadius: '100px',
        },
    };
}

export function LeadershipProfiles({ sectionData, lazyLoad }: LeadershipProfilesProps): React.ReactElement | null {
    const { title, categories, profiles } = sectionData;
    const [isMobile] = useAtom(isMobileAtom);
    const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
    const swiperRef = useRef<SwiperType | null>(null);

    const contentWithId = sectionData.profiles.map((card, index) => ({
        ...card,
        id: `${card.name}-${index}`,
    }));
    const hasProfileBioCards = contentWithId.filter((card) => !!card.popOutWindow.description?.length);
    const validatedTabIndex =
        activeTabIndex >= categories.length && categories.length > 0 ? categories.length - 1 : activeTabIndex;
    const [openProfileIndex, setOpenProfileIndex] = useState<number | null>(null);

    const filteredProfiles = useMemo(() => {
        if (categories.length === 0) {
            return [];
        }

        const activeCategoryName = categories[activeTabIndex];

        return profiles.filter((profile) => {
            const isInCorrectCategory = profile.assignedCategories.includes(activeCategoryName);
            const hasBiography = profile.popOutWindow.description.length;
            return isInCorrectCategory && hasBiography;
        });
    }, [activeTabIndex, categories, profiles]);

    useEffect(() => {
        swiperRef.current?.update();
    }, [validatedTabIndex]);

    useEffect(() => {
        if (validatedTabIndex !== activeTabIndex) {
            setActiveTabIndex(validatedTabIndex);
        }
    }, [validatedTabIndex, activeTabIndex]);

    function switchTab(_event: React.SyntheticEvent, newIndex: number): void {
        setActiveTabIndex(newIndex);
        swiperRef.current?.slideTo(newIndex);
    }

    function handleSlideChange(swiper: SwiperType): void {
        setActiveTabIndex(swiper.activeIndex);
    }

    function handleKeydownOnProfile(event: React.KeyboardEvent, clickedProfile: Profile): void {
        if (keyIsEnter(event) || keyIsSpace(event)) {
            event.preventDefault();
            const indexInFilteredList = filteredProfiles.findIndex((p) => p.name === clickedProfile.name);
            if (indexInFilteredList > -1) {
                setOpenProfileIndex(indexInFilteredList);
            }
        }
    }

    const handleProfileClick = (clickedProfile: Profile): void => {
        const indexInFilteredList = filteredProfiles.findIndex((p) => p.name === clickedProfile.name);

        if (indexInFilteredList > -1) {
            setOpenProfileIndex(indexInFilteredList);
        }
    };

    const handleClosePopout = (): void => {
        setOpenProfileIndex(null);
    };

    const handleIncrementPopoutIndex = (increment: number): void => {
        if (openProfileIndex === null) return;
        setOpenProfileIndex(openProfileIndex + increment);
    };

    const tabClasses = getRoundedTabStyles();

    if (!categories || categories.length === 0 || !profiles || profiles.length === 0) {
        return null;
    }

    return (
        <SectionContainer data-testid='LeadershipProfilesComponent'>
            <Grid container direction='column' alignItems='center'>
                <Heading align='center' variant='h2'>
                    {title}
                </Heading>
                <Tabs
                    value={validatedTabIndex}
                    onChange={switchTab}
                    css={tabClasses.container}
                    variant='scrollable'
                    scrollButtons={isMobile ? false : 'auto'}
                    slotProps={{
                        scrollButtons: {
                            sx: classes.scrollButtons,
                        },
                        indicator: {
                            sx: tabClasses.indicator,
                            children: <span className='MuiTabs-indicatorSpan' />,
                        },
                    }}
                >
                    {categories.map((categoryName, index) => (
                        <Tab key={index} label={categoryName} css={tabClasses.tab} disableRipple />
                    ))}
                </Tabs>
                <Swiper
                    key={JSON.stringify(categories)}
                    css={classes.fullWidth}
                    modules={[Keyboard, A11y]}
                    onBeforeInit={(swiper) => {
                        swiperRef.current = swiper;
                    }}
                    initialSlide={validatedTabIndex}
                    onSlideChange={handleSlideChange}
                    keyboard={{
                        enabled: true,
                    }}
                    a11y={{}}
                    speed={0}
                    cssMode={false}
                    spaceBetween={0}
                    slidesPerView={1}
                    allowTouchMove={true}
                    autoHeight={true}
                >
                    {categories.length > 0
                        ? categories.map((categoryName, index) => {
                            const profilesForTab = contentWithId.filter((p) =>
                                p.assignedCategories.includes(categoryName)
                            );
                            const isHidden = validatedTabIndex !== index;

                            return (
                                <SwiperSlide key={index}>
                                    <div
                                        role='tabpanel'
                                        data-testid={`tabpanel-${index}`}
                                        css={classes.tabPanel(isHidden)}
                                        id={`profiles-tabpanel-${index}`}
                                        aria-labelledby={`profiles-tab-${index}`}
                                    >
                                        <LazyLoadContainer
                                            component={Grid}
                                            container
                                            lazyload={lazyLoad}
                                            justifyContent='center'
                                            alignItems='stretch'
                                            spacing={{ xs: 2, md: 4 }}
                                        >
                                            {profilesForTab.length > 0 ? (
                                                profilesForTab.map((profile) => {
                                                    const popoutIndex = hasProfileBioCards.findIndex(
                                                        (bioCard) => bioCard.id === profile.id
                                                    );
                                                    const hasPopout = popoutIndex > -1;

                                                    return (
                                                        <Grid
                                                            size={{ xs: 12, sm: 4, md: 3 }}
                                                            key={profile.id}
                                                            css={classes.cardContainer(hasPopout)}
                                                            onClick={
                                                                hasPopout
                                                                    ? () => handleProfileClick(profile)
                                                                    : undefined
                                                            }
                                                            onKeyDown={(e) => handleKeydownOnProfile(e, profile)}
                                                            role={hasPopout ? 'button' : undefined}
                                                            tabIndex={hasPopout ? 0 : -1}
                                                            aria-label={
                                                                hasPopout
                                                                    ? `View full popup profile for ${profile.name}`
                                                                    : undefined
                                                            }
                                                        >
                                                            <Stack>
                                                                {profile.image.imagePath ? (
                                                                    <div css={classes.imageWrapper}>
                                                                        <Picture
                                                                            imagePath={profile.image.imagePath}
                                                                            mobilePath={profile.image.mobilePath}
                                                                            alt={profile.image.imageAlt}
                                                                            css={classes.image}
                                                                            lazyLoad={lazyLoad}
                                                                            preload={!lazyLoad}
                                                                            className='profile-image'
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <Skeleton
                                                                        variant='rounded'
                                                                        height={280}
                                                                        css={classes.imageWrapper}
                                                                    />
                                                                )}
                                                                <Text
                                                                    variant='h4'
                                                                    weight='bold'
                                                                    component='h3'
                                                                    css={classes.name}
                                                                >
                                                                    {profile.name}
                                                                </Text>
                                                                <Text variant='body2'>{profile.title}</Text>
                                                            </Stack>
                                                        </Grid>
                                                    );
                                                })
                                            ) : (
                                                <Grid size={{ xs: 12 }}>
                                                    <Text variant='body2' align='center'>
                                                          There are no profiles in this category.
                                                    </Text>
                                                </Grid>
                                            )}
                                        </LazyLoadContainer>
                                    </div>
                                </SwiperSlide>
                            );
                        })
                        : null}
                </Swiper>
            </Grid>
            <ImagePopout
                imageBoxes={filteredProfiles}
                openIndex={openProfileIndex}
                onClose={handleClosePopout}
                onIncrementIndex={handleIncrementPopoutIndex}
            />
        </SectionContainer>
    );
}
